import * as bynderAxios from '../../helpers/customAxios';
import { isCollectionDetailView } from '../../helpers/windowLocation';
import {
	SpotlightMediaSearchResponse,
	SpotlightMetapropertiesSearchResponse,
	SpotlightSearchParams,
	SearchResults,
	TypesOfSpotlightRequests,
	SpotlightMediaSearchInCollectionResponse,
	SpotlightCollectionsSearchResponse,
	SpotlightGuidelinesSearchResponse,
} from './types';
import { attachHighlighting } from './helpers/attachHighlighting';

export const spotlightAPI = {
	searchSpotlightAssets: (
		searchTerm: string,
		params?: SpotlightSearchParams,
		{
			useHybridSearch = false,
			signal,
		}: { useHybridSearch?: boolean; signal?: AbortSignal } = {},
	): Promise<SpotlightMediaSearchResponse | SpotlightMediaSearchInCollectionResponse> => {
		// Backend rule: the hybrid endpoint is only for a search term.
		const spotlightAssetsUrl =
			useHybridSearch && searchTerm.trim()
				? '/search/spotlight/hybrid-assets/'
				: '/search/spotlight/assets/';

		return bynderAxios.get({
			url: isCollectionDetailView() ? '/search/collection/' : spotlightAssetsUrl,
			config: {
				params: { keyword: searchTerm, jsonv2: 'true', ...params },
				signal,
			},
		});
	},

	searchSpotlightCollections: (
		searchTerm: string,
		params?: SpotlightSearchParams,
		signal?: AbortSignal,
	): Promise<SpotlightCollectionsSearchResponse> => {
		return bynderAxios.get({
			url: `${window.location.origin}/v7/collection/api/v3/collections/spotlight`,
			config: {
				params: { searchTerm, ...params },
				signal,
			},
		});
	},

	searchSpotlightMetaproperties: (
		searchTerm: string,
		params?: SpotlightSearchParams,
		signal?: AbortSignal,
	): Promise<SpotlightMetapropertiesSearchResponse> => {
		return bynderAxios.get({
			url: '/search/spotlight/metaproperties/',
			config: {
				params: { keyword: searchTerm, jsonv2: 'true', ...params },
				signal,
			},
		});
	},

	searchGuide: (
		searchTerm: string,
		signal?: AbortSignal,
	): Promise<SpotlightGuidelinesSearchResponse> => {
		return bynderAxios.get({
			url: '/v7/guidelines-search/documents/spotlight',
			config: {
				params: { searchTerm },
				signal,
			},
			jwt: true,
		});
	},
};

/*
 * searchSpotlightAssets returns one of two shapes depending on the endpoint it hit. They are
 * distinguishable at runtime by `results`: /search/spotlight/assets/ returns a flat array of assets,
 * while /search/collection/ nests them under `results.media` ({ docs, numFound }).
 */
export const isCollectionSearchResponse = (
	response: SpotlightMediaSearchResponse | SpotlightMediaSearchInCollectionResponse,
): response is SpotlightMediaSearchInCollectionResponse => !Array.isArray(response.results);

// Each sibling call resolves to its response under the SearchResults key it fills
export type SpotlightSiblingResult =
	| {
			key: typeof TypesOfSpotlightRequests.Collections;
			response: SpotlightCollectionsSearchResponse;
	  }
	| {
			key: typeof TypesOfSpotlightRequests.Metaproperties;
			response: SpotlightMetapropertiesSearchResponse;
	  }
	| {
			key: typeof TypesOfSpotlightRequests.Guidelines;
			response: SpotlightGuidelinesSearchResponse;
	  };

export const getAPISpotlightCalls = (
	term: string,
	params: SpotlightSearchParams | undefined,
	{ canShowBrandguidelines, signal }: { canShowBrandguidelines: boolean; signal?: AbortSignal },
): Promise<SpotlightSiblingResult>[] => [
	spotlightAPI
		.searchSpotlightCollections(term, params, signal)
		.catch(() => ({ results: [], count: 0 }))
		.then((response) => ({ key: TypesOfSpotlightRequests.Collections, response })),
	spotlightAPI
		.searchSpotlightMetaproperties(term, params, signal)
		.catch(() => ({ results: [], count: 0 }))
		.then((response) => ({ key: TypesOfSpotlightRequests.Metaproperties, response })),
	...(canShowBrandguidelines
		? [
				spotlightAPI
					.searchGuide(term, signal)
					.catch(() => ({ results: [] }))
					.then((response) => ({ key: TypesOfSpotlightRequests.Guidelines, response })),
		  ]
		: []),
];

// A response can be a 200 with no `results` — e.g. the login page HTML when an anonymous user's
// request is redirected — so anything that isn't a list becomes an empty one.
const toList = <L extends unknown[]>(results: L | undefined): L | [] =>
	Array.isArray(results) ? results : [];

export const handleSpotlightResults = (results: SpotlightSiblingResult[]) =>
	results.reduce<Partial<SearchResults>>((groups, result) => {
		switch (result.key) {
			case TypesOfSpotlightRequests.Metaproperties:
				return { ...groups, [result.key]: toList(result.response?.results) };
			case TypesOfSpotlightRequests.Collections:
			case TypesOfSpotlightRequests.Guidelines: {
				const items = toList(result.response?.results);

				return {
					...groups,
					[result.key]: { items, total: result.response?.count ?? items.length },
				};
			}
			default:
				return groups;
		}
	}, {});

// searchSpotlightAssets picks its endpoint internally, so the response is the reliable signal of
// which shape came back. It can also be the login page HTML (see `toList`), so totals fall back to 0.
export const handleSpotlightAssetsResponse = (
	response: SpotlightMediaSearchResponse | SpotlightMediaSearchInCollectionResponse,
): Pick<SearchResults, 'assets' | 'tags'> => ({
	assets: isCollectionSearchResponse(response)
		? {
				items: attachHighlighting(
					toList(response.results?.media?.docs),
					response.highlighting,
				),
				total: response.results?.media?.numFound ?? 0,
		  }
		: {
				items: attachHighlighting(toList(response.results), response.highlighting),
				total: response.count ?? 0,
		  },
	tags: toList(response.tags),
});
